import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, KeyRound, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { COACH_ROLE_LABELS } from "./trackNav";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";
const EMPTY = { first_name: "", last_name: "", email: "", username: "", password: "", organization_id: "", coach_role: "head_coach", status: "active" };

export default function TrackCoaches() {
  const [coaches, setCoaches] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([api.get("/track/coaches"), api.get("/track/organizations")]);
      setCoaches(c.data); setOrgs(o.data);
    } catch { toast.error("Failed to load coaches."); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const orgName = (id) => orgs.find((o) => o.id === id)?.name || "—";

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      if (!body.organization_id) delete body.organization_id;
      const { data } = await api.post("/track/coaches", body);
      toast.success(`Coach created — @${data.username} can log in now.`);
      setForm(EMPTY); setShowPw(false); setShowAdd(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const patch = async (id, updates) => { try { await api.patch(`/track/coaches/${id}`, updates); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const reset = async (c) => {
    const np = window.prompt(`New password for ${c.name} (min 8 chars):`);
    if (!np) return;
    try { await api.post(`/track/coaches/${c.id}/reset-password`, { new_password: np }); toast.success("Password reset."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (c) => { if (!window.confirm(`Delete coach ${c.name}?`)) return; try { await api.delete(`/track/coaches/${c.id}`); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); } };

  return (
    <div data-testid="track-coaches">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Coaches</h1>
          <p className="text-white/40 text-sm mt-1" data-testid="track-coach-count">{coaches.length} total</p>
        </div>
        <button data-testid="track-add-coach" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "New Coach"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-8 grid sm:grid-cols-3 gap-4" data-testid="track-coach-form">
          <div><label className={labelCls}>First Name *</label><input data-testid="coach-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Last Name *</label><input data-testid="coach-last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Email (optional)</label><input data-testid="coach-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Username *</label><input data-testid="coach-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} className={inputCls} /></div>
          <div><label className={labelCls}>Password *</label>
            <div className="relative">
              <input data-testid="coach-password" required type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 8 chars" className={`${inputCls} pr-10`} />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>
            </div>
          </div>
          <div><label className={labelCls}>Organization</label>
            <select data-testid="coach-org" value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} className={inputCls}>
              <option value="">— None —</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Coach Role</label>
            <select data-testid="coach-role" value={form.coach_role} onChange={(e) => setForm({ ...form, coach_role: e.target.value })} className={inputCls}>
              {Object.entries(COACH_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3"><button data-testid="coach-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Coach</button></div>
        </form>
      )}

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>
        : coaches.length === 0 ? <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="track-no-coaches">No coaches yet. Create your first coach account above.</div>
        : (
          <div className="border border-white/10 bg-[#121212] overflow-x-auto" data-testid="track-coach-list">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-white/40">{["Name", "Username", "Role", "Organization", "Status", ""].map((h, i) => <th key={i} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {coaches.map((c) => (
                  <tr key={c.id} data-testid={`track-coach-${c.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3 font-bold">{c.name}</td>
                    <td className="px-4 py-3 text-white/60">@{c.username}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`coach-role-${c.id}`} value={c.coach_role} onChange={(e) => patch(c.id, { coach_role: e.target.value })} className="bg-[#0A0A0A] border border-white/15 px-2 py-1 text-xs">
                        {Object.entries(COACH_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select data-testid={`coach-org-${c.id}`} value={c.organization_id || ""} onChange={(e) => patch(c.id, { organization_id: e.target.value })} className="bg-[#0A0A0A] border border-white/15 px-2 py-1 text-xs">
                        <option value="">— None —</option>
                        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`coach-status-${c.id}`} onClick={() => patch(c.id, { status: c.status === "active" ? "disabled" : "active" })} className={`font-mono-accent text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 transition-colors ${c.status === "active" ? "bg-white text-black border-white" : "text-white/50 border-white/25"}`}>{c.status}</button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`coach-reset-${c.id}`} onClick={() => reset(c)} title="Reset password" className="text-white/40 hover:text-white transition-colors"><KeyRound size={15} /></button>
                      <button data-testid={`coach-delete-${c.id}`} onClick={() => del(c)} title="Delete" className="ml-4 text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
